import { ApiError } from '@/api';

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface AppError {
  code: string;
  title: string;
  message: string;
  severity: ErrorSeverity;
  retryable: boolean;
  field?: string;
  context?: Record<string, unknown>;
}

export const ErrorCode = {
  Conflict: 'ADM_CONFLICT',
  Validation: 'ADM_VALIDATION',
  NotFound: 'ADM_NOT_FOUND',
  Unauthorised: 'ADM_UNAUTHORISED',
  Forbidden: 'ADM_FORBIDDEN',
  SessionExpired: 'ADM_SESSION_EXPIRED',
  AccountLocked: 'ADM_ACCOUNT_LOCKED',
  InvalidCredentials: 'ADM_INVALID_CREDENTIALS',
  InvalidToken: 'ADM_INVALID_TOKEN',
  ExpiredToken: 'ADM_EXPIRED_TOKEN',
  ImportInvalid: 'ADM_IMPORT_INVALID',
  MigrationIncompatible: 'ADM_MIGRATION_INCOMPATIBLE',
  Network: 'ADM_NETWORK_UNAVAILABLE',
  Timeout: 'ADM_REQUEST_TIMEOUT',
  Unexpected: 'ADM_UNEXPECTED',
} as const;
export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export class AppErrorException extends Error {
  readonly appError: AppError;

  constructor(appError: AppError) {
    super(appError.message);
    this.name = appError.code;
    this.appError = appError;
  }
}

export function createAppError(
  code: string,
  title: string,
  message: string,
  options: Partial<Omit<AppError, 'code' | 'title' | 'message'>> = {},
): AppError {
  return {
    code,
    title,
    message,
    severity: options.severity ?? 'error',
    retryable: options.retryable ?? false,
    ...(options.field === undefined ? {} : { field: options.field }),
    ...(options.context === undefined ? {} : { context: options.context }),
  };
}

export function conflictError(entityType: string, entityId: string): AppError {
  return createAppError(
    ErrorCode.Conflict,
    'This record changed elsewhere',
    'Someone else updated this record while you were editing. Reload the latest values and apply your change again. Your unsaved input has been preserved.',
    { severity: 'warning', retryable: true, context: { entityType, entityId } },
  );
}

export function networkError(): AppError {
  return createAppError(
    ErrorCode.Network,
    'No connection',
    'Check your connection and try again.',
    { severity: 'warning', retryable: true },
  );
}

export function requestTimeoutError(): AppError {
  return createAppError(
    ErrorCode.Timeout,
    'Request timed out',
    'The server took too long to respond. Refresh before saving again — the change may already have been saved.',
    { severity: 'warning', retryable: true },
  );
}

const API_ERROR_TITLES: Record<string, string> = {
  ADM_SESSION_EXPIRED: 'Session expired',
};

function transportCode(error: Error): string | undefined {
  if ('code' in error && typeof error.code === 'string') return error.code;
  return undefined;
}

/**
 * Client abort after the hang bound. The request may already have been
 * committed, so this is not an offline failure and must not share that message.
 */
export function isRequestTimeout(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const code = transportCode(error);
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true;
  return error.message.toLowerCase().startsWith('timeout of ');
}

/**
 * True for a request that never reached the API — browser `fetch` offline,
 * axios `Network Error`, Safari `Load failed`. Not a 4xx/5xx envelope and not
 * a client timeout (`isRequestTimeout`).
 */
export function isNetworkFailure(error: unknown): boolean {
  if (!(error instanceof Error) || isRequestTimeout(error)) return false;
  if (error.name === 'NetworkError') return true;
  if (transportCode(error) === 'ERR_NETWORK') return true;
  const message = error.message.toLowerCase();
  return (
    message === 'failed to fetch' ||
    message === 'network error' ||
    message === 'load failed' ||
    message.includes('networkerror when attempting to fetch')
  );
}

export function toAppError(error: unknown): AppError {
  if (error instanceof AppErrorException) return error.appError;
  if (error instanceof ApiError) {
    const code = error.envelope.messageCode;
    if (code === ErrorCode.SessionExpired) {
      return createAppError(ErrorCode.SessionExpired, 'Session expired', error.envelope.message, {
        severity: 'warning',
        retryable: false,
      });
    }
    return createAppError(
      code,
      API_ERROR_TITLES[code] ?? 'Request failed',
      error.envelope.message,
      {
        severity: error.status >= 500 ? 'error' : 'warning',
        retryable: error.status >= 500,
      },
    );
  }
  if (isRequestTimeout(error)) return requestTimeoutError();
  if (isNetworkFailure(error)) return networkError();
  if (error instanceof Error) {
    return createAppError(ErrorCode.Unexpected, 'Something went wrong', error.message, {
      retryable: true,
    });
  }
  return createAppError(
    ErrorCode.Unexpected,
    'Something went wrong',
    'An unexpected error occurred.',
    { retryable: true },
  );
}
